import { Tabs as RadixTabs } from "radix-ui";
import type { ReactNode } from "react";
import { cn } from "../lib/cn";

export interface TabItem {
	value: string;
	label: ReactNode;
	count?: number;
	disabled?: boolean;
}

export interface TabsProps {
	items: readonly TabItem[];
	value?: string;
	defaultValue?: string;
	onValueChange?: (value: string) => void;
	label?: string;
	className?: string;
	children?: ReactNode;
}

/**
 * Пилюля на серой подложке: контейнер r12 с паддингом 4, вкладка 40px r8.
 * Активная — белая с мягкой тенью; никакой зелёной полосы здесь нет, «где я»
 * в витрине отмечает боковая навигация, а не фильтр внутри карточки.
 *
 * Ховер только меняет цвет подписи: подъём на 1px из DS для IEC ID снят —
 * бренд-бриф запрещает подъём в ховере.
 */
export function Tabs({
	items,
	value,
	defaultValue,
	onValueChange,
	label,
	className,
	children,
}: TabsProps) {
	return (
		<RadixTabs.Root
			value={value}
			defaultValue={defaultValue ?? items[0]?.value}
			onValueChange={onValueChange}
			className={cn("flex flex-col gap-5", className)}
		>
			<RadixTabs.List
				aria-label={label}
				className="inline-flex w-fit items-center gap-1 rounded-card bg-sunken p-1"
			>
				{items.map((item) => (
					<RadixTabs.Trigger
						key={item.value}
						value={item.value}
						disabled={item.disabled}
						className={cn(
							"iec-press inline-flex h-10 items-center gap-1.5 rounded-field-sm px-[18px] t-body",
							"text-fg-secondary",
							"hover:text-fg",
							"data-[state=active]:bg-card data-[state=active]:text-fg data-[state=active]:shadow-pill",
							"focus-ring",
							"disabled:cursor-not-allowed disabled:opacity-60",
						)}
					>
						{item.label}
						{item.count != null ? (
							<span className="t-num-xs text-fg-muted">{item.count}</span>
						) : null}
					</RadixTabs.Trigger>
				))}
			</RadixTabs.List>
			{children}
		</RadixTabs.Root>
	);
}

export const TabsPanel = RadixTabs.Content;
